import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const FAQS = [
  { question: 'How fast can you deliver a project?',
    answer: 'Most MVPs ship in 2–4 weeks. Full-featured applications typically take 4–8 weeks depending on complexity. We provide a detailed timeline during the free discovery call, and we guarantee on-time delivery or your money back.' },
  { question: 'What does "AI-augmented" actually mean?',
    answer: 'Every team member uses AI-powered tools purpose-built for their domain — code generation for boilerplate, AI-assisted design prototyping, automated testing, and intelligent code review. This lets our 8-person team deliver the output of a 20-person agency, faster and with fewer bugs.' },
  { question: 'Do I own the source code?',
    answer: 'Yes, 100%. From day one, all source code, designs, and assets belong to you. We use your GitHub repository, and you have full access throughout development. No lock-in, no proprietary frameworks.' },
  { question: 'How does pricing work?',
    answer: 'We provide fixed-price quotes after the discovery call — no hourly billing, no scope creep surprises. Projects range from $3K for simple iOS apps to $50K+ for enterprise platforms. You get a detailed breakdown before any work begins.' },
  { question: 'What happens after launch?',
    answer: 'Every project includes 30 days of free post-launch support — bug fixes, performance monitoring, and minor adjustments. After that, we offer ongoing maintenance plans or you can take over fully since you own all the code.' },
  { question: 'Can you work with our existing codebase?',
    answer: 'Absolutely. We regularly take over existing projects for refactoring, feature additions, or performance optimization. We start with a codebase audit to understand the current state and propose improvements.' },
  { question: 'What if I need changes mid-project?',
    answer: 'We build in weekly review cycles specifically for this. Minor scope adjustments are included. For larger changes, we provide a clear cost and timeline impact before proceeding — no surprises.' },
  { question: 'How do I communicate with the team?',
    answer: 'You get a dedicated project manager (Jacob) as your single point of contact, plus a shared Slack or Discord channel with the full team. Weekly video demos on staging, daily async updates, and direct access to developers when needed.' },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="r-section faq" id="faq">
      <div className="r-wrap">
        <div className="faq-grid">
          <div className="faq-intro r-reveal">
            <div className="r-tag">FAQ</div>
            <h2 className="r-head" style={{ marginBottom: '8px' }}>
              Questions? <span className="r-gtxt">Answers.</span>
            </h2>
            <p>
              Everything you need to know before starting a project with us. Can&apos;t find your
              answer? <a href="#contact">Get in touch</a>.
            </p>
            <div className="faq-call">
              <div className="faq-call-h">✦ Free discovery call</div>
              <div className="faq-call-p">
                30 minutes, no commitment. We&apos;ll discuss your project and give you a clear
                scope, timeline, and fixed-price quote.
              </div>
            </div>
          </div>

          <div className="faq-list">
            {FAQS.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={faq.question} className={`faq-item ${isOpen ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="faq-q"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="faq-a-inner">{faq.answer}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
